import { useEffect } from 'react'

interface SeoProps {
	title?: string
	description?: string
	canonical?: string
	robots?: string
	openGraph?: {
		title?: string
		description?: string
		url?: string
		type?: string
		image?: string
	}
}

export function Seo({ title, description, canonical, robots, openGraph }: SeoProps) {
	useEffect(() => {
		const head = document.head
		const prevTitle = document.title
		if (title) document.title = title

		function upsertMeta(name: string, content?: string) {
			if (!content) return () => {}
			let el = head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
			if (!el) {
				el = document.createElement('meta')
				el.setAttribute('name', name)
				head.appendChild(el)
			}
			const prev = el.getAttribute('content')
			el.setAttribute('content', content)
			return () => {
				if (prev === null) {
					head.removeChild(el!)
				} else {
					el!.setAttribute('content', prev)
				}
			}
		}

		function upsertProperty(property: string, content?: string) {
			if (!content) return () => {}
			let el = head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null
			if (!el) {
				el = document.createElement('meta')
				el.setAttribute('property', property)
				head.appendChild(el)
			}
			const prev = el.getAttribute('content')
			el.setAttribute('content', content)
			return () => {
				if (prev === null) {
					head.removeChild(el!)
				} else {
					el!.setAttribute('content', prev)
				}
			}
		}

		function upsertLink(rel: string, href?: string) {
			if (!href) return () => {}
			let el = head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
			if (!el) {
				el = document.createElement('link')
				el.setAttribute('rel', rel)
				head.appendChild(el)
			}
			const prev = el.getAttribute('href')
			el.setAttribute('href', href)
			return () => {
				if (prev === null) {
					head.removeChild(el!)
				} else {
					el!.setAttribute('href', prev)
				}
			}
		}

		const cleanups: Array<() => void> = []
		cleanups.push(upsertMeta('description', description))
		cleanups.push(upsertMeta('robots', robots))
		cleanups.push(upsertProperty('og:title', openGraph?.title ?? title))
		cleanups.push(upsertProperty('og:description', openGraph?.description ?? description))
		cleanups.push(upsertProperty('og:url', openGraph?.url ?? canonical))
		cleanups.push(upsertProperty('og:type', openGraph?.type ?? 'website'))
		cleanups.push(upsertProperty('og:image', openGraph?.image))
		cleanups.push(upsertLink('canonical', canonical))

		return () => {
			if (title) document.title = prevTitle
			cleanups.forEach(fn => fn())
		}
	}, [title, description, canonical, robots, openGraph?.title, openGraph?.description, openGraph?.url, openGraph?.type, openGraph?.image])

	return null
}

export default Seo


